// @ts-nocheck
/**
 * mapLayerOps.ts
 * MapLibreレイヤーのopacity/visibility操作を一元管理
 * Dialog-layer.vueの4箇所の重複コードを統一
 */

// レイヤータイプ → opacity プロパティ名の対応表
const OPACITY_PROP: Record<string, string> = {
  raster: 'raster-opacity',
  fill: 'fill-opacity',
  line: 'line-opacity',
  'fill-extrusion': 'fill-extrusion-opacity',
  heatmap: 'heatmap-opacity',
  circle: 'circle-opacity',
  symbol: 'text-opacity',
  background: 'background-opacity',
}

// フェードイン設定
const FADE_DURATION_MS = 400  // フェードイン時間

/**
 * レイヤーをフェードイン付きで追加
 * 1. opacityを0にしてaddLayer
 * 2. transition付きで本来のopacityに戻す
 */
export function addLayerWithFadeIn(map: unknown, layerDef: unknown, beforeLayer?: string, parentOpacity?: number): void {
  const prop = OPACITY_PROP[layerDef.type]

  if (!prop) {
    // opacity未対応タイプはそのまま追加
    if (beforeLayer) {
      map.addLayer(layerDef, beforeLayer)
    } else {
      map.addLayer(layerDef)
    }
    return
  }

  // 元のopacity値を保存
  const originalOpacity = layerDef.paint?.[prop]

  // opacityを0にしたコピーを作成（元オブジェクトは変更しない）
  const fadeLayer = {
    ...layerDef,
    paint: {
      ...(layerDef.paint || {}),
      [prop]: 0,
      [`${prop}-transition`]: { duration: FADE_DURATION_MS, delay: 0 }
    }
  }

  if (beforeLayer) {
    map.addLayer(fadeLayer, beforeLayer)
  } else {
    map.addLayer(fadeLayer)
  }

  // parentOpacity指定時はsetLayerOpacityが直後に正しい値を設定するのでrAF不要
  // （rAFはsetLayerOpacityの後に発火し、乗算opacityを上書きしてしまう）
  if (parentOpacity !== undefined) return

  // 次フレームで本来のopacityを設定（transitionで滑らかにアニメーション）
  requestAnimationFrame(() => {
    try {
      const targetOpacity = originalOpacity !== undefined ? originalOpacity : 1
      map.setPaintProperty(layerDef.id, prop, targetOpacity)
    } catch { /* レイヤーが既に削除されている場合 */ }
  })
}

/**
 * 単一サブレイヤーにopacityを設定
 * - max-opacity対応
 * - osm-bright/osm-3dのfill-opacity乗算対応
 * - oh-homusyo-2025-polygonのスキップ対応
 */
export function setLayerOpacity(map: unknown, layer0: unknown, parentOpacity: number): void {
  const prop = OPACITY_PROP[layer0.type]
  if (!prop) return

  let opacity = layer0['max-opacity']
    ? parentOpacity * layer0['max-opacity']
    : parentOpacity

  // oh-homusyo-2025-polygon は固定opacity（スキップ）
  if (layer0.id === 'oh-homusyo-2025-polygon') return

  // osm-bright / osm-3d の fill レイヤーは元のopacityとの乗算
  if (layer0.type === 'fill' && layer0.metadata &&
      (layer0.metadata.group === 'osm-bright' || layer0.metadata.group === 'osm-3d')) {
    if (typeof layer0.paint?.['fill-opacity'] === 'number') {
      opacity = layer0.paint['fill-opacity'] * opacity
    }
  }

  try {
    map.setPaintProperty(layer0.id, prop, opacity)
  } catch { /* レイヤー未追加時等 */ }
}

/**
 * 単一サブレイヤーにvisibilityを設定
 */
export function setLayerVisibility(map: unknown, layerId: string, visible: unknown): void {
  const vis = visible === false ? 'none' : 'visible'
  try {
    map.setLayoutProperty(layerId, 'visibility', vis)
  } catch { /* ignore */ }
}

/**
 * selectedLayerの全サブレイヤーにopacity + visibilityを適用
 * これ1つで4箇所の重複コードを置換
 */
export function applyOpacityAndVisibility(map: unknown, layer: unknown): void {
  if (!layer.layers) return
  for (const layer0 of layer.layers) {
    if (!map.getLayer(layer0.id)) continue
    setLayerOpacity(map, layer0, layer.opacity)
    setLayerVisibility(map, layer0.id, layer.visibility)
  }
}
